import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { trackByRef } from 'src/app/lego-utils';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { NzMessageService } from 'ng-zorro-antd/message';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { IUser } from 'src/app/models';
import { SharePointService } from 'src/app/services';
import { AdminService, IAdmin } from 'src/app/state';

@Component({
  selector: 'em-create-admin-drawer',
  templateUrl: './create-admin-drawer.component.html',
  standalone: false,
})
export class CreateAdminDrawerComponent implements OnInit, OnDestroy {
  public formGroup: FormGroup<{ user: FormControl<IAdmin | undefined> }>;
  public isSaving = false;
  public isLoading = false;
  public errorMsg?: string;
  public readonly trackByRef = trackByRef.bind(undefined);
  public optionList?: IUser[];

  private readonly subscriptions = new Subscription();
  private readonly searchChange$ = new Subject<string>();

  public constructor(
    private readonly formBuilder: FormBuilder,
    private readonly adminService: AdminService,
    private readonly drawerRef: NzDrawerRef,
    private readonly messageService: NzMessageService,
    private readonly sharePointService: SharePointService
  ) {
    const requiredValidator = Validators.required.bind(Validators);

    this.formGroup = this.formBuilder.group({
      user: this.formBuilder.control<IAdmin | undefined>(undefined, {
        nonNullable: true,
        validators: [requiredValidator],
      }),
    });
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.searchChange$
        .pipe(
          debounceTime(500),
          switchMap((searchValue) => {
            // lookups on empty searchValues causes 400 bad request
            if (!searchValue) {
              return of([]);
            }

            return this.sharePointService.searchUserProfiles(searchValue).pipe(
              catchError((error: unknown) => {
                if (error instanceof Error) {
                  this.errorMsg = error.message;
                } else {
                  this.errorMsg = 'Unknown error occurred';
                }

                return of([]);
              })
            );
          })
        )
        .subscribe((users) => {
          this.optionList = users;
          this.isLoading = false;
        })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public submitForm(): void {
    this.isSaving = true;
    const user = this.formGroup.value.user;

    if (!user) {
      this.isSaving = false;

      return;
    }

    const messageId = this.messageService.loading(`Sending data to server...`).messageId;

    this.subscriptions.add(
      this.adminService.create(user).subscribe({
        next: () => {
          this.messageService.remove(messageId);
          this.messageService.create('success', 'Successfully added admin');
          this.drawerRef.close();
        },
        error: (error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          this.messageService.remove(messageId);
          this.messageService.create('error', errorMessage);
          this.isSaving = false;
        },
      })
    );
  }

  public onSearch(searchValue: string): void {
    this.isLoading = true;
    this.optionList = undefined;
    this.errorMsg = undefined;
    this.searchChange$.next(searchValue);
  }
}
